import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  FaCheckCircle,
  FaEnvelope,
  FaHeart,
  FaHome,
  FaInfoCircle,
  FaUserFriends,
} from "react-icons/fa";
import playstore from "../assets/images/playstore.png";
import appstore from "../assets/images/appstore.png";
import instagram from "../assets/images/instagram.png";
import { Button, buttonClass } from "../ui";
import AuthLayout from "../components/site/AuthLayout";
import {
  APP_STORE_URL,
  INSTAGRAM_URL,
  PLAY_STORE_URL,
} from "../constants/links";

const SOCIAL_LINKS = [
  { href: INSTAGRAM_URL, img: instagram, alt: "Instagram" },
  { href: PLAY_STORE_URL, img: playstore, alt: "Google Play" },
  { href: APP_STORE_URL, img: appstore, alt: "App Store" },
];

// One titled block inside the farewell card.
const Block = ({ icon: Icon, title, children }) => (
  <section className="rounded-xl bg-white/[0.05] border border-white/10 p-5">
    <h2 className="font-bold text-white mb-2 flex items-center gap-2">
      <Icon className="text-primary shrink-0" aria-hidden="true" />
      {title}
    </h2>
    <div className="text-sm text-white/70 leading-relaxed">{children}</div>
  </section>
);

const AccountDeletedGoodbye = () => {
  const location = useLocation();
  const { userName, timestamp } = location.state || {};

  useEffect(() => {
    // Clear any remaining user data
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies if any
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }, []);

  return (
    <AuthLayout
      wide
      title={`وداعاً ${userName || "صديق تربع"}`}
      subtitle="شكراً لك على الأوقات الجميلة التي قضيناها معاً"
      footer={
        <p className="italic">
          "كل نهاية هي بداية جديدة، وكل وداع هو لقاء آخر"
        </p>
      }
    >
      {/* Success Message */}
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-5">
          <FaCheckCircle className="text-2xl" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-extrabold">تم حذف حسابك بنجاح</h2>
        <p className="text-white/70 mt-2 leading-relaxed">
          تم حذف جميع بياناتك نهائياً من خوادمنا كما طلبت. نحترم قرارك ونتمنى
          لك كل الخير.
        </p>

        {timestamp && (
          <p className="mt-3 text-sm text-white/55">
            تم الحذف في:{" "}
            {new Date(timestamp).toLocaleString("ar-SA", {
              numberingSystem: "latn",
            })}
          </p>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <Block icon={FaHeart} title="شكراً لكونك جزءاً من عائلة تربع">
          <p>
            سعدنا بوجودك معنا ونأمل أن تكون قد استمتعت بالأوقات التي قضيتها في
            لعبة تربع. رحلتك معنا كانت مميزة وستبقى في ذاكرتنا.
          </p>
        </Block>

        <Block icon={FaInfoCircle} title="ماذا بعد الآن؟">
          <ul className="list-disc pr-5 space-y-1">
            <li>تم حذف جميع بياناتك نهائياً ولن تتمكن من استردادها</li>
            <li>
              لن تتمكن من إنشاء حساب جديد بنفس البريد الإلكتروني لمدة 30 يوماً
            </li>
            <li>إذا غيرت رأيك، يمكنك العودة إلينا في أي وقت بحساب جديد</li>
          </ul>
        </Block>

        <Block icon={FaUserFriends} title="أبوابنا مفتوحة دائماً">
          <p>
            إذا قررت العودة في المستقبل، ستجدنا هنا نرحب بك بأذرع مفتوحة. مجتمع
            تربع سيكون دائماً بيتك الثاني.
          </p>
        </Block>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 space-y-3">
        <Button
          to="/"
          size="lg"
          block
          icon={<FaHome aria-hidden="true" />}
        >
          زيارة موقع تربع
        </Button>

        <a
          href="mailto:tarbaa.kw@gmail.com"
          className={buttonClass({ variant: "secondary", block: true })}
        >
          <FaEnvelope aria-hidden="true" />
          <span>تواصل معنا</span>
        </a>
      </div>

      {/* Social Links */}
      <div className="mt-8 pt-6 border-t border-white/10 text-center">
        <h2 className="text-sm font-semibold text-white/70 mb-4">
          تابعنا على وسائل التواصل الاجتماعي
        </h2>

        <div className="flex justify-center gap-4">
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.alt}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <img
                src={s.img}
                alt={s.alt}
                className="w-12 h-12 rounded-xl shadow-lg"
              />
            </a>
          ))}
        </div>

        <p className="mt-6 text-xs text-white/55">
          © {new Date().getFullYear()} تربع - شكراً لك ومع السلامة
        </p>
      </div>
    </AuthLayout>
  );
};

export default AccountDeletedGoodbye;
